import { Monitor, Moon, Sun } from 'lucide-react';
import { useNavigate } from 'react-router-dom';

import { useCategoriesStore } from '@/features/categories/store';
import { useGroupsStore } from '@/features/groups/store';
import { useLedgerStore } from '@/features/ledger/store';
import { useSettingsStore, type ThemeMode } from '@/features/settings/store';
import { BASE_URL } from '@/lib/api';
import { Button, Card, CardHeader, PageHeader, SegmentedControl, Select, Toggle } from '@/ui';
import { SUPPORTED_CURRENCIES, currencySymbol } from '@domain/money';

const THEME_SEGMENTS: { value: ThemeMode; label: string }[] = [
  { value: 'system', label: 'System' },
  { value: 'light', label: 'Light' },
  { value: 'dark', label: 'Dark' },
];

const THEME_ICONS = { system: Monitor, light: Sun, dark: Moon };

export function SettingsPage() {
  const navigate = useNavigate();

  const themeMode = useSettingsStore((state) => state.themeMode);
  const setThemeMode = useSettingsStore((state) => state.setThemeMode);
  const defaultCurrency = useSettingsStore((state) => state.defaultCurrency);
  const setDefaultCurrency = useSettingsStore((state) => state.setDefaultCurrency);
  const showInactive = useSettingsStore((state) => state.showInactiveMembers);
  const setShowInactive = useSettingsStore((state) => state.setShowInactiveMembers);
  const pageSize = useSettingsStore((state) => state.pageSize);
  const setPageSize = useSettingsStore((state) => state.setPageSize);

  const groups = useGroupsStore((state) => state.groups);
  const categories = useCategoriesStore((state) => state.categories);
  const members = useLedgerStore((state) => state.members);
  const expenses = useLedgerStore((state) => state.allExpenses);

  const ThemeIcon = THEME_ICONS[themeMode];

  return (
    <>
      <PageHeader title="Settings" />

      <div className="grid gap-4 lg:grid-cols-2">
        <Card>
          <CardHeader title="Appearance" description="Applies to this browser only." />
          <div className="flex items-center gap-3">
            <SegmentedControl
              value={themeMode}
              onChange={setThemeMode}
              segments={THEME_SEGMENTS}
            />
            <ThemeIcon size={16} className="text-muted" />
          </div>
          <p className="mt-2.5 text-[12px] text-muted">
            &ldquo;System&rdquo; follows your operating system and updates live if you change it.
          </p>
        </Card>

        <Card>
          <CardHeader title="Defaults" />
          <div className="flex flex-col gap-4">
            <Select
              label="Default currency for new groups"
              value={defaultCurrency}
              onChange={(event) => setDefaultCurrency(event.target.value)}
              options={SUPPORTED_CURRENCIES.map((code) => ({
                value: code,
                label: `${code} — ${currencySymbol(code)}`,
              }))}
            />

            <Select
              label="Rows per page in tables"
              value={String(pageSize)}
              onChange={(event) => setPageSize(Number(event.target.value))}
              options={[10, 25, 50, 100].map((size) => ({
                value: String(size),
                label: `${size} rows`,
              }))}
            />

            <div className="flex items-center justify-between gap-4">
              <span className="min-w-0">
                <span className="block text-sm">Show inactive members</span>
                <span className="block text-[12px] text-muted">
                  Keeps deactivated people visible in balance lists.
                </span>
              </span>
              <Toggle
                checked={showInactive}
                onChange={setShowInactive}
                label="Show inactive members"
              />
            </div>
          </div>
        </Card>

        <Card>
          <CardHeader title="Data" description="Everything is loaded from and saved to the API." />

          <dl className="flex flex-col gap-2.5 text-[13px]">
            <Row label="API" value={BASE_URL} />
            <Row label="Groups" value={String(groups.length)} />
            <Row label="Categories" value={String(categories.length)} />
            <Row label="In open group" value={`${members.length} members · ${expenses.length} expenses`} />
          </dl>
        </Card>

        <Card>
          <CardHeader title="Manage" />
          <div className="flex flex-wrap gap-2">
            <Button variant="secondary" onClick={() => navigate('/groups')}>
              Groups ({groups.length})
            </Button>
            <Button variant="secondary" onClick={() => navigate('/members')}>
              Members ({members.length})
            </Button>
            <Button variant="secondary" onClick={() => navigate('/categories')}>
              Categories ({categories.length})
            </Button>
          </div>
        </Card>
      </div>

      <Card className="mt-4">
        <CardHeader title="Keyboard shortcuts" />
        <dl className="grid gap-2 text-[13px] sm:grid-cols-2">
          <Shortcut keys="N" description="Add an expense" />
          <Shortcut keys="/" description="Focus the expense search box" />
          <Shortcut keys="Esc" description="Close the open dialog" />
          <Shortcut keys="Enter" description="Open the focused table row" />
        </dl>
      </Card>
    </>
  );
}

function Row({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex items-center justify-between gap-4">
      <dt className="text-muted">{label}</dt>
      <dd className="truncate text-right">{value}</dd>
    </div>
  );
}

function Shortcut({ keys, description }: { keys: string; description: string }) {
  return (
    <div className="flex items-center gap-2.5">
      <kbd className="inline-flex h-6 min-w-6 items-center justify-center rounded-md border border-line bg-elevated px-1.5 font-mono text-[11px]">
        {keys}
      </kbd>
      <span className="text-muted">{description}</span>
    </div>
  );
}
